import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, type Location } from 'react-router-dom';
import { direQueLInterfaceADemarre } from './native/maj';
import { NotifEnApp } from './components/layout/NotifEnApp';
import { OuvertureNotif } from './components/layout/OuvertureNotif';
import type { ReactNode } from 'react';
import { ScreenLoader } from './components/layout/Screens';
import { TabBar } from './components/layout/TabBar';
import { Ecrans } from './components/layout/Ecrans';
import { TabSwipe } from './components/layout/TabSwipe';
import { TabNavProvider } from './components/layout/TabNav';
import { useBoutonRetour } from './hooks/useBoutonRetour';
import { useSession } from './store/SessionContext';

import { LoginScreen } from './screens/auth/LoginScreen';
import { RegisterScreen } from './screens/auth/RegisterScreen';
import { ChooseSpotScreen } from './screens/auth/ChooseSpotScreen';
import { ForgotPasswordScreen } from './screens/auth/ForgotPasswordScreen';

import { FeedScreen } from './screens/feed/FeedScreen';
import { ComposeScreen } from './screens/feed/ComposeScreen';
import { BookmarksScreen } from './screens/feed/BookmarksScreen';
import { MyPostsScreen } from './screens/feed/MyPostsScreen';
import { CommentsScreen } from './screens/feed/CommentsScreen';
import { CommentThreadScreen } from './screens/feed/CommentThreadScreen';

import { EventsScreen } from './screens/events/EventsScreen';
import { CreateEventScreen } from './screens/events/CreateEventScreen';
import { AgendaScreen } from './screens/events/AgendaScreen';

/*
  La carte des sorties se charge a part : la bibliotheque de cartographie
  pese plus que tout le reste de l'onglet, et la plupart des membres
  n'ouvriront pas la carte a chaque visite.
*/
const EventsMapScreen = lazy(() =>
  import('./screens/events/EventsMapScreen').then((module) => ({ default: module.EventsMapScreen })),
);
import { EventDetailScreen } from './screens/events/EventDetailScreen';
import { EventManageScreen } from './screens/events/EventManageScreen';
import { EventParticipantsScreen } from './screens/events/EventParticipantsScreen';
import { EventRequestsScreen } from './screens/events/EventRequestsScreen';
import { MyEventsScreen } from './screens/events/MyEventsScreen';
import { HiddenEventsScreen } from './screens/events/HiddenEventsScreen';

import { MessagesScreen } from './screens/messages/MessagesScreen';
import { ConversationScreen } from './screens/messages/ConversationScreen';
import { PersonalizeScreen } from './screens/auth/PersonalizeScreen';
import { MessageRequestsScreen } from './screens/messages/MessageRequestsScreen';
import { AddFriendsScreen } from './screens/messages/AddFriendsScreen';
import { NewGroupScreen } from './screens/messages/NewGroupScreen';
import { GroupInfoScreen } from './screens/messages/GroupInfoScreen';
import { AddMembersScreen } from './screens/messages/AddMembersScreen';

import { LieuxScreen } from './screens/lieux/LieuxScreen';
import { AnnuaireScreen } from './screens/annuaire/AnnuaireScreen';
import { AnnuaireEtudiantsScreen } from './screens/annuaire/AnnuaireEtudiantsScreen';
import { AnnuaireFormationScreen } from './screens/annuaire/AnnuaireFormationScreen';
import { AssociationsScreen } from './screens/annuaire/AssociationsScreen';
import { AssociationScreen } from './screens/annuaire/AssociationScreen';

import { ProfileScreen } from './screens/profile/ProfileScreen';
import { MemberProfileScreen } from './screens/profile/MemberProfileScreen';
import { DestinationsScreen } from './screens/profile/DestinationsScreen';
import { ModerationScreen } from './screens/moderation/ModerationScreen';
import { AdminScreen } from './screens/moderation/AdminScreen';
import { AvertissementRecu } from './screens/moderation/AvertissementRecu';
import { MiseAJourDispo } from './screens/legal/MiseAJourDispo';
import { SettingsScreen } from './screens/profile/SettingsScreen';
import { TableauScreen } from './screens/profile/TableauScreen';
import { ProfileVisibilityScreen } from './screens/profile/ProfileVisibilityScreen';
import { AccountScreen } from './screens/profile/AccountScreen';
import { PasswordScreen } from './screens/profile/PasswordScreen';
import { BlockedScreen } from './screens/profile/BlockedScreen';
import { HiddenAccountsScreen } from './screens/profile/HiddenAccountsScreen';
import { HiddenBlockedScreen } from './screens/profile/HiddenBlockedScreen';
import { HiddenPostsScreen } from './screens/profile/HiddenPostsScreen';
import { HelpScreen } from './screens/profile/HelpScreen';
import { LangueScreen } from './screens/profile/LangueScreen';
import { FriendsListScreen } from './screens/profile/FriendsListScreen';
import { NotificationsScreen } from './screens/profile/NotificationsScreen';
import { ReportScreen } from './screens/report/ReportScreen';
import { ConfirmerAdresseScreen } from './screens/auth/ConfirmerAdresseScreen';
import { ActionScreen } from './screens/auth/ActionScreen';
import { ConsentGate } from './screens/legal/ConsentGate';
import { LegalScreen } from './screens/legal/LegalScreen';
import { auth as authApi, events, users as usersApi } from './api/endpoints';

/**
 * Routage de l'application.
 *
 * Trois zones :
 *  - `/login`, `/register`      : ouvertes a tous ;
 *  - `/choose-spot`             : reserve aux membres connectes ;
 *  - `/app/*`                   : reserve aux membres qui ont un spot.
 *
 * La barre d'onglets n'apparait que sur les cinq ecrans principaux ;
 * les ecrans annexes occupent tout l'ecran.
 */

export function App() {
  const { user, loading } = useSession();

  /*
    Un ecran ouvert par-dessus un autre.

    Ouvrir une page depuis une carte demontait la carte : au
    retour, elle se reconstruisait — tuiles rechargees, fond gris, un
    temps d'arret pour un aller-retour. Le lien porte donc l'adresse d'ou
    il part (`fond`) : les routes se lisent la, l'ecran du dessous reste
    monte, et la page de la sortie vient se poser dessus. Revenir en
    arriere la retire, et l'on retrouve la carte telle qu'on l'a laissee.

    Tout le reste de l'application ignore ce mecanisme : sans `fond`,
    c'est l'adresse qui commande, comme toujours.
  */
  const emplacement = useLocation();
  const fond = (emplacement.state as { fond?: Location } | null)?.fond ?? null;

  /*
    L'interface dit a la coquille qu'elle a demarre.

    Sur iPhone, l'application peut remplacer son interface sans passer par
    l'App Store. Une version qui ne s'afficherait pas laisserait un ecran
    blanc que rien ne pourrait plus corriger, puisque c'est elle qui devait
    le faire. Cette parole est le filet : sans elle, la coquille jette la
    version au lancement suivant et revient a celle qu'elle embarque.

    Elle se dit d'ici plutot que du point d'entree : un module qui
    s'execute ne prouve rien, un effet qui part prouve que l'arbre est
    monte et que l'ecran s'affiche.
  */
  useEffect(direQueLInterfaceADemarre, []);

  /*
    Le compteur de kilometres servait aux trophees, et son interrupteur est parti avec eux :
    un telephone qui l'avait allume continuerait de suivre le membre sans moyen de l'arreter.
  */
  useEffect(() => {
    void events.eteindreLeCompteurDeKm();
  }, []);

  /* Sur Android, le retour recule dans l'application au lieu d'en sortir. */
  useBoutonRetour(Boolean(user));

  /*
    Et elle dit aussi laquelle elle est.

    Sans ce mot, personne ne peut savoir qui a deja la derniere version de
    l'App Store et qui tourne encore sur la precedente. Un echec ne se
    rattrape pas : ce n'est qu'un releve.
  */
  useEffect(() => {
    if (!user) return;
    void usersApi.noterMaVersion().catch(() => undefined);
  }, [user]);

  /*
    Et la langue dans laquelle on la lit.

    Les notifications et les courriels partent du serveur, qui ne voit pas
    le telephone : c'est ici qu'il apprend dans quelle langue ecrire.
  */
  useEffect(() => {
    if (!user) return;
    void usersApi.retenirMaLangue().catch(() => undefined);
  }, [user?.id]);

  if (loading) {
    return (
      <div className="app-frame">
        <ScreenLoader tone="brand" />
      </div>
    );
  }

  return (
    <TabNavProvider>
      <div className="app-frame">
        {/* La banniere des messages, quand l'application est ouverte :
            c'est le seul moment ou iOS n'affiche rien. */}
        <NotifEnApp />
        {/* Une notification touchee mene a ce qu'elle annonce. */}
        <OuvertureNotif />
        {/* Un avertissement de la moderation se dresse devant l'ecran et
            attend d'etre lu : c'est le seul geste qui parle au membre. */}
        <AvertissementRecu />
        {/* Une version que l'application ne peut pas se donner toute seule
            se dit ici : le reste arrive sans qu'on ait rien a faire. */}
        <MiseAJourDispo />
        <Routes>
          {/* --- Public --------------------------------------------- */}
          <Route path="/login" element={<GuestOnly>{<LoginScreen />}</GuestOnly>} />
          <Route path="/register" element={<GuestOnly>{<RegisterScreen />}</GuestOnly>} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          {/*
            Ce que les liens des courriels ouvrent. Publique par nature :
            on y arrive sans etre connecte, c'est meme tout l'objet.
          */}
          <Route path="/action" element={<ActionScreen />} />

          {/*
            Juste apres l'inscription : on propose une photo et deux
            lignes avant d'entrer. L'ecran se passe volontiers — il ne
            garde personne — d'ou une simple route, sans redirection qui
            y ramenerait.
          */}
          <Route
            path="/personalize"
            element={
              <RequireAuth>
                <PersonalizeScreen />
              </RequireAuth>
            }
          />

          {/* --- Connecte, spot pas encore choisi -------------------- */}
          <Route
            path="/choose-spot"
            element={
              <RequireAuth>
                <ChooseSpotScreen />
              </RequireAuth>
            }
          />

          {/* --- Dans un spot --------------------------------------- */}
          <Route
            path="/app/*"
            element={
              <RequireSpot>
                <TabSwipe>
                  {/* Lues au fond quand un ecran s'ouvre par-dessus : celui du dessous reste monte. */}
                  <Ecrans fond={fond}>
                  <Routes>
                    <Route path="feed" element={<FeedScreen />} />
                    <Route path="feed/compose" element={<ComposeScreen />} />
                    <Route path="feed/bookmarks" element={<BookmarksScreen />} />
                    <Route path="feed/mine" element={<MyPostsScreen />} />
                    <Route path="feed/:postId/comments" element={<CommentsScreen />} />
                    {/* Le fil d'une reponse : elle s'ouvre comme un post s'ouvre. */}
                    <Route
                      path="feed/:postId/comments/:commentId"
                      element={<CommentThreadScreen />}
                    />

                    <Route path="events" element={<EventsScreen />} />
                    <Route path="events/new" element={<CreateEventScreen />} />
                    <Route path="events/agenda" element={<AgendaScreen />} />
                    <Route
                      path="events/carte"
                      element={
                        <Suspense fallback={<ScreenLoader />}>
                          <EventsMapScreen />
                        </Suspense>
                      }
                    />
                    <Route path="events/mine" element={<MyEventsScreen />} />
                    <Route path="events/hidden" element={<HiddenEventsScreen />} />
                    <Route path="events/:eventId" element={<EventDetailScreen />} />
                    <Route path="events/:eventId/manage" element={<EventManageScreen />} />
                    <Route
                      path="events/:eventId/participants"
                      element={<EventParticipantsScreen />}
                    />
                    <Route path="events/:eventId/demandes" element={<EventRequestsScreen />} />

                    <Route path="messages" element={<MessagesScreen />} />
                    <Route path="messages/friends" element={<AddFriendsScreen />} />
                    <Route path="messages/requests" element={<MessageRequestsScreen />} />
                    <Route path="messages/new-group" element={<NewGroupScreen />} />
                    <Route path="messages/:conversationId" element={<ConversationScreen />} />
                    <Route path="messages/:conversationId/info" element={<GroupInfoScreen />} />
                    <Route path="messages/:conversationId/add" element={<AddMembersScreen />} />

                    <Route path="lieux" element={<AvecLieux><LieuxScreen /></AvecLieux>} />

                    <Route path="annuaire" element={<AvecAnnuaire><AnnuaireScreen /></AvecAnnuaire>} />
                    <Route
                      path="annuaire/etudiants"
                      element={<AvecAnnuaire><AnnuaireEtudiantsScreen /></AvecAnnuaire>}
                    />
                    <Route
                      path="annuaire/etudiants/:formation"
                      element={<AvecAnnuaire><AnnuaireFormationScreen /></AvecAnnuaire>}
                    />
                    <Route
                      path="annuaire/associations"
                      element={<AvecAnnuaire><AssociationsScreen /></AvecAnnuaire>}
                    />
                    <Route
                      path="annuaire/associations/:asso"
                      element={<AvecAnnuaire><AssociationScreen /></AvecAnnuaire>}
                    />

                    <Route path="profile" element={<ProfileScreen />} />
                    <Route path="profile/destinations" element={<DestinationsScreen />} />
                    <Route path="profile/friends" element={<FriendsListScreen />} />
                    <Route path="profile/settings" element={<SettingsScreen />} />
                    {/* La file de moderation. La route existe pour tout le
                        monde ; la fonction qui agit derriere verifie le role,
                        et l'entree des reglages ne parait qu'aux moderateurs. */}
                    <Route path="profile/moderation" element={<ModerationScreen />} />
                    <Route path="profile/administration" element={<AdminScreen />} />
                    <Route
                      path="profile/settings/visibility"
                      element={<ProfileVisibilityScreen />}
                    />
                    <Route path="profile/settings/account" element={<AccountScreen />} />
                    <Route path="profile/settings/password" element={<PasswordScreen />} />
                    <Route
                      path="profile/settings/notifications"
                      element={<NotificationsScreen />}
                    />
                    <Route path="profile/tableau" element={<TableauScreen />} />
                    <Route path="profile/settings/blocked" element={<BlockedScreen />} />
                    <Route path="profile/settings/masques" element={<HiddenBlockedScreen />} />
                    <Route
                      path="profile/settings/masques/comptes"
                      element={<HiddenAccountsScreen />}
                    />
                    <Route
                      path="profile/settings/masques/posts"
                      element={<HiddenPostsScreen />}
                    />
                    <Route
                      path="profile/settings/masques/sorties"
                      element={<HiddenEventsScreen />}
                    />
                    <Route path="profile/settings/help" element={<HelpScreen />} />
                    <Route path="profile/settings/langue" element={<LangueScreen />} />
                    <Route path="profile/settings/legal" element={<LegalScreen />} />
                    <Route path="members/:userId" element={<MemberProfileScreen />} />

                    {/* Un seul ecran de signalement, pour tout ce qui se signale. */}
                    <Route path="report" element={<ReportScreen />} />

                    <Route path="*" element={<Navigate to="/app/feed" replace />} />
                  </Routes>
                  </Ecrans>
                </TabSwipe>

                {/* Ce qui s'ouvre par-dessus, sans rien demonter dessous. */}
                {fond && (
                  <div className="route-dessus">
                    <Routes>
                      <Route path="events/:eventId" element={<EventDetailScreen />} />
                      {/* Depuis un lieu : revenir retrouve la carte telle qu'on l'a laissee. */}
                      <Route path="events/new" element={<CreateEventScreen />} />
                    </Routes>
                  </div>
                )}
              </RequireSpot>
            }
          />

          {/* --- Entree par defaut ---------------------------------- */}
          <Route path="*" element={<Navigate to={landingFor(user)} replace />} />
        </Routes>

        {/* Elle decide seule si elle a lieu d'etre : voir `TabBar`. */}
        <TabBar />
      </div>
    </TabNavProvider>
  );
}

/** Ou envoyer quelqu'un qui arrive a la racine. */
function landingFor(user: { currentServerId: string | null } | null): string {
  if (!user) return '/login';
  return user.currentServerId ? '/app/feed' : '/choose-spot';
}

/** Un visiteur deja connecte n'a rien a faire sur la connexion. */
function GuestOnly({ children }: { children: ReactNode }) {
  const { user } = useSession();
  if (user) return <Navigate to={landingFor(user)} replace />;
  return <>{children}</>;
}

/**
 * Connecte, et adresse confirmee.
 *
 * Une adresse non confirmee est une adresse que personne ne peut
 * reprendre : ni pour retrouver son mot de passe, ni pour prevenir avant
 * la suppression d'un compte endormi. Tant que le lien recu n'a pas ete
 * suivi, l'application ne s'ouvre pas — c'est ce que dit la politique de
 * confidentialite, et c'est ce qu'elle fait.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useSession();
  if (!user) return <Navigate to="/login" replace />;
  if (!authApi.isVerified()) return <ConfirmerAdresseScreen />;
  /*
    La porte du reconsentement vient apres celle de l'adresse : demander
    d'accepter des documents a quelqu'un dont on ne sait pas encore s'il
    existe n'aurait pas de sens.
  */
  return <ConsentGate>{children}</ConsentGate>;
}

/** L'interieur de l'application n'existe qu'a l'interieur d'un spot. */
function RequireSpot({ children }: { children: ReactNode }) {
  const { user } = useSession();
  if (!user) return <Navigate to="/login" replace />;
  if (!authApi.isVerified()) return <ConfirmerAdresseScreen />;
  if (!user.currentServerId) return <Navigate to="/choose-spot" replace />;
  return <ConsentGate>{children}</ConsentGate>;
}

/**
 * L'onglet Lieux, sauf sur un serveur qui l'a remplace par son annuaire : un lien
 * ancien ou une adresse tapee a la main y menent encore.
 */
function AvecLieux({ children }: { children: ReactNode }) {
  const { server } = useSession();
  if (server?.sansTrophees) return <Navigate to="/app/feed" replace />;
  return <>{children}</>;
}

/** L'annuaire, seulement la ou l'onglet existe : ailleurs, retour au fil. */
function AvecAnnuaire({ children }: { children: ReactNode }) {
  const { server } = useSession();
  if (!server?.sansTrophees || !server.annuaire) return <Navigate to="/app/feed" replace />;
  return <>{children}</>;
}
